import React, { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { styled, useResponsiveValue } from 'dripsy';
import { useNavigation } from '@react-navigation/native';
import { useModal } from 'react-native-modalfy';

import {
  BaseText,
  Separator,
  Icon,
  BaseTextLink,
  NoTrackFeedbackMonthlySvg,
  Button,
  SubscribeSuggestionModalTypeEnum,
} from '@components';
import { screens, TabNavigatorNavigationPropType } from '@navigation';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { TrackFeedbackSubmitOptionEnum } from '@api';
import { modals, ModalStackParamsType } from '@modals';
import { IS_MOBILE, IS_WEB } from '@utils';

const EmptyResultBlock = styled(View)({
  flex: 1,
  alignItems: 'center',
  flexDirection: ['row', 'column'],
});

const ResultBlock = styled(View)({
  backgroundColor: '$background10',
  px: ['$5', '$10'],
  width: ['100%', 350],
  py: ['$6', '$8'],
  borderRadius: 4,
});

const HintTextContainer = styled(View)({
  flexShrink: 1,
});

const IconContainer = styled(View)({
  width: 24,
  height: 24,
  borderRadius: 24,
  alignItems: 'center',
  justifyContent: 'center',
});

const FillAllContainer = styled(View)({
  flex: 1,
});

const HintTitle = styled(BaseText)({
  color: '$heading800',
  variant: 'text.small',
  fontWeight: 'semi',
});

const HintDescription = styled(BaseText)({
  color: '$heading800',
  variant: 'text.small',
});

const LinkTextTouchable = styled(BaseTextLink)({
  color: '$heading800',
  variant: 'text.small',
});

const NoResultTitle = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
  fontWeight: 'semi',
  textAlign: 'center',
  flex: 2,
});

const NoTrackFeedbackMonthlySvgStyled = styled(
  NoTrackFeedbackMonthlySvg,
)({
  height: [100, 145],
  width: [120, 170],
});

const ChooseTrackFeedbackOptionButton = styled(Button)({
  width: '100%',
});

type NavigationPropType =
  TabNavigatorNavigationPropType<'TrackFeedback'>;

export const ResultView = observer(() => {
  const { t } = useTranslation();
  // TODO:: Make vertical prop is responsive by default and remove this hook
  const isVertical = useResponsiveValue([true, false]);
  const svgHeight = useResponsiveValue([100, 130]);
  const svgWidth = useResponsiveValue([120, 150]);
  const nav = useNavigation<NavigationPropType>();
  const track = useTracksModel();
  const modal = useModal<ModalStackParamsType>();

  const isNewPeriod = track.feedback.isNewPeriod;
  const nextDate = track.feedback.nextDateFreeFeedback;
  const hasCredits = !!track.feedback.credits?.count;
  const isShowTrackForFeedbackButton = isNewPeriod || hasCredits;

  function navigateToContactUs() {
    nav.navigate(screens.FeedbackNavigator, {
      screen: screens.ContactUs,
    });
  }

  function navigateToSubscription() {
    nav.navigate(screens.ProfileNavigator, {
      screen: screens.Subscription,
    });
  }

  function onNavigateToTrackFeedback(
    feedbackOptionType: TrackFeedbackSubmitOptionEnum | null,
  ) {
    nav.navigate(screens.FeedbackNavigator, {
      screen: screens.TrackFeedbackForm,
      params: { feedbackOptionType },
    });
  }

  function openChooseTrackFeedbackOptionModal() {
    if (!track.feedback.isAvailableForCurrentPlan) {
      modal.openModal(modals.SubscribeSuggestionModal, {
        onPress: () => {
          navigateToSubscription();
          modal.closeModal();
        },
        type: SubscribeSuggestionModalTypeEnum.TrackFeedback,
      });
    } else {
      modal.openModal(modals.ChooseTrackFeedbackOptionModal, {
        onNavigateToTrackFeedback,
      });
    }
  }

  return (
    <ResultBlock>
      {isNewPeriod ? (
        <EmptyResultBlock>
          <NoTrackFeedbackMonthlySvgStyled
            height={svgHeight}
            width={svgWidth}
          />

          <Separator vertical={isVertical} size="$6" />
          <FillAllContainer>
            <NoResultTitle>
              {`${t('trackFeedback.noTrackFeedbackMonthly')} 🗒️`}
            </NoResultTitle>
            {IS_MOBILE && isShowTrackForFeedbackButton && (
              <Fragment>
                <Separator />
                <ChooseTrackFeedbackOptionButton
                  onPress={openChooseTrackFeedbackOptionModal}
                >
                  {t('trackFeedback.addTrackButtonTitle')}
                </ChooseTrackFeedbackOptionButton>
              </Fragment>
            )}
          </FillAllContainer>
        </EmptyResultBlock>
      ) : (
        <Fragment>
          <IconContainer>
            <Icon
              size={20}
              name="info-circule-bold"
              color="$background40"
            />
          </IconContainer>
          <Separator vertical size="$4" />
          <HintTextContainer>
            <HintTitle>{t('trackFeedback.hintTitle')}</HintTitle>
            <Separator size="$4" />
            <Fragment>
              <HintDescription>
                {t('trackFeedback.hintDescription', {
                  date: nextDate,
                })}
              </HintDescription>
              {hasCredits && (
                <Fragment>
                  <Separator size="$4" />
                  <HintDescription>
                    {t('trackFeedback.hintHasCreditsDescription', {
                      date: nextDate,
                    })}
                  </HintDescription>
                </Fragment>
              )}
              <Separator size="$4" />
            </Fragment>
            <HintDescription>
              {t('trackFeedback.hintSubDescription')}{' '}
              <LinkTextTouchable onPress={navigateToContactUs}>
                {t('trackFeedback.hintLink')}
              </LinkTextTouchable>
            </HintDescription>
          </HintTextContainer>
        </Fragment>
      )}
      {(!isNewPeriod || IS_WEB) && isShowTrackForFeedbackButton && (
        <Fragment>
          <Separator />
          <ChooseTrackFeedbackOptionButton
            onPress={openChooseTrackFeedbackOptionModal}
          >
            {t('trackFeedback.addTrackButtonTitle')}
          </ChooseTrackFeedbackOptionButton>
        </Fragment>
      )}
    </ResultBlock>
  );
});
